import type { Post } from "../data/models";
import { isFeatured } from "../data/models";
import {
  absoluteDay,
  daysUntil,
  eventWhen,
  compactCount,
  money,
  relativeShort,
} from "../data/format";
import { openTelegram } from "../platform/telegram";
import { MediaWell } from "./MediaWell";
import {
  Avatar,
  AvatarStack,
  Button,
  Card,
  CourseBadge,
  HandoffButton,
  Pill,
  TypeChip,
} from "./primitives";

/**
 * The polymorphic card. One skeleton, six types.
 *
 *   attribution → media slot → title → type payload → footer
 *
 * Types differ by the chip dot and the payload only. The card is never
 * re-themed per type — the single visual override is the featured wash, which
 * is a promotion state rather than a type.
 */

export interface PostCardActions {
  onRSVP?: (post: Post) => void;
  onJoin?: (post: Post) => void;
  onAcceptRival?: (post: Post) => void;
  onDeclineRival?: (post: Post) => void;
  onApply?: (post: Post) => void;
  onLike?: (post: Post) => void;
  onOpen?: (post: Post) => void;
}

export function PostCard({
  post,
  compact = false,
  actions = {},
}: {
  post: Post;
  compact?: boolean;
  actions?: PostCardActions;
}) {
  const featured = isFeatured(post);

  return (
    <Card
      compact={compact}
      featured={featured}
      onClick={actions.onOpen ? () => actions.onOpen?.(post) : undefined}
    >
      {featured ? (
        <div style={{ padding: "8px var(--space-4) 0" }}>
          <span className="t-micro" style={{ color: "var(--brand-tint-text)" }}>
            Featured
          </span>
        </div>
      ) : null}

      <Media post={post} />

      <div className="card__body">
        <Attribution post={post} />

        <TypeChip type={post.postType} />

        {post.title ? <div className="card__title">{post.title}</div> : null}

        {/* The untitled `regular` variant is a named case, not an omission —
            posts are body-first by nature. */}
        {post.content && post.postType === "regular" ? (
          <div className="card__content">{post.content}</div>
        ) : null}

        <Payload post={post} />
      </div>

      <Footer post={post} actions={actions} />
    </Card>
  );
}

/* ---------------- Slots ---------------- */

function Media({ post }: { post: Post }) {
  // Typed cards are payload-first — an event leads with when/where, a
  // scholarship with its amount. Their seeded thumbnails are generic filler,
  // and a media well would push the payload below the fold.
  if (post.postType !== "regular") return null;

  const url = post.mediaURL ?? post.thumbnailURL;
  if (!url) return null;

  // Learn content is landscape-native; everything else in a feed is 4:5.
  const aspect = post.courseId ? "learn" : "feed";
  return <MediaWell url={url} type={post.mediaType} aspect={aspect} />;
}

function Attribution({ post }: { post: Post }) {
  const club = post.club;
  const name = club?.name ?? post.author.displayName;
  const photo = club?.profilePhotoURL ?? post.author.profilePhotoURL;

  const meta: string[] = [];
  if (club) meta.push("club");
  else if (post.author.school) meta.push(`${post.author.school} '29`);
  if (post.isInvited && post.invitedBy) meta.push(`invited you`);
  else if (post.postType === "rival") meta.push("challenged you");
  meta.push(relativeShort(post.createdAt));

  return (
    <div className="card__attribution">
      <Avatar id={post.author.id} name={name} photoURL={photo} size={30} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="card__author">
          {name}
          {post.author.isVerified ? (
            <i
              className="fa-solid fa-circle-check"
              style={{ color: "var(--blue-500)", fontSize: 10, marginLeft: 5 }}
              aria-label="verified"
            />
          ) : null}
        </div>
        <div className="card__meta">{meta.join(" · ")}</div>
      </div>
    </div>
  );
}

function Payload({ post }: { post: Post }) {
  switch (post.postType) {
    case "event":
      return <EventPayload post={post} />;
    case "activity":
      return <ActivityPayload post={post} />;
    case "rival":
      return <RivalPayload post={post} />;
    case "scholarship":
      return <ScholarshipPayload post={post} />;
    case "job":
      return <JobPayload post={post} />;
    default:
      return <RegularPayload post={post} />;
  }
}

function RegularPayload({ post }: { post: Post }) {
  return (
    <div className="card__row">
      {post.courseCode ? <CourseBadge code={post.courseCode} compact /> : null}
      {post.sharedCourse ? <CourseBadge code={post.sharedCourse} compact suffix="shared" /> : null}
      {post.hashtags.slice(0, 2).map((tag) => (
        <span key={tag} className="t-caption">
          {tag}
        </span>
      ))}
    </div>
  );
}

function EventPayload({ post }: { post: Post }) {
  const attending = post.matchesAttending ?? [];
  return (
    <div className="card__payload">
      <div className="t-caption" style={{ color: "var(--text)" }}>
        {eventWhen(post.eventDate, post.eventEndDate)}
      </div>
      {post.eventLocation || post.isVirtual ? (
        <div className="t-caption">
          <i className="fa-solid fa-location-dot" aria-hidden="true" />{" "}
          {post.isVirtual ? "Online" : post.eventLocation}
        </div>
      ) : null}

      {attending.length > 0 ? (
        <div className="card__row">
          <AvatarStack people={attending} size={24} />
          <span className="t-caption">
            {attending.length === 1
              ? "1 of your matches is going"
              : `${attending.length} of your matches are going`}
            {post.attendeeCount ? ` · ${compactCount(post.attendeeCount)} total` : ""}
          </span>
        </div>
      ) : post.attendeeCount ? (
        <div className="t-caption">{compactCount(post.attendeeCount)} going</div>
      ) : null}
    </div>
  );
}

function ActivityPayload({ post }: { post: Post }) {
  return (
    <div className="card__payload">
      <div className="card__row">
        {post.category ? <Pill tone="meta">{titleCase(post.category)}</Pill> : null}
        {post.isOpen ? <Pill tone="meta">Open to all</Pill> : null}
        {post.respondentCount !== undefined ? (
          <Pill tone="meta">{post.respondentCount} joined</Pill>
        ) : null}
      </div>
      {post.eventDate ? (
        <div className="t-caption" style={{ color: "var(--text)" }}>
          {eventWhen(post.eventDate, post.eventEndDate)}
          {post.eventLocation ? ` · ${post.eventLocation}` : ""}
        </div>
      ) : post.eventLocation ? (
        <div className="t-caption">{post.eventLocation}</div>
      ) : null}
    </div>
  );
}

function RivalPayload({ post }: { post: Post }) {
  return (
    <div className="card__payload">
      <div className="card__row">
        {post.sharedCourse ? (
          <CourseBadge code={post.sharedCourse} compact suffix="shared course" />
        ) : post.category ? (
          <Pill tone="meta">{titleCase(post.category)}</Pill>
        ) : null}
      </div>
      {post.eventDate ? (
        <div className="t-caption">{eventWhen(post.eventDate, post.eventEndDate)}</div>
      ) : null}
      {post.acceptedBy ? (
        <div className="t-caption">Accepted by {post.acceptedBy.displayName}</div>
      ) : null}
    </div>
  );
}

function ScholarshipPayload({ post }: { post: Post }) {
  const days = daysUntil(post.applicationDeadline);
  return (
    <div className="card__payload">
      {post.scholarshipAmount ? (
        <div className="t-display" style={{ fontSize: 24, color: "var(--text-strong)" }}>
          {money(post.scholarshipAmount)}
        </div>
      ) : null}

      {/* The "opportunity finds you" beat — pre-qualification is the whole
          point, so it leads the payload rather than sitting in the footer. */}
      <div className="card__row">
        <Pill tone="qualify">
          <i className="fa-solid fa-circle-check" aria-hidden="true" /> You pre-qualify
        </Pill>
      </div>

      {post.eligibilityCriteria ? (
        <div className="card__row">
          {post.eligibilityCriteria
            .split(",")
            .slice(0, 3)
            .map((c) => (
              <Pill key={c} tone="meta">
                {c.trim()}
              </Pill>
            ))}
        </div>
      ) : null}

      <Deadline iso={post.applicationDeadline} days={days} />
    </div>
  );
}

function JobPayload({ post }: { post: Post }) {
  return (
    <div className="card__payload">
      {post.compensationAmount ? (
        <div className="t-title" style={{ color: "var(--text-strong)" }}>
          {post.compensationAmount}
        </div>
      ) : null}
      <div className="card__row">
        {post.isRemote ? <Pill tone="meta">Remote</Pill> : <Pill tone="meta">On-site</Pill>}
        {post.location ? <Pill tone="meta">{post.location}</Pill> : null}
      </div>
      <Deadline iso={post.applicationDeadline} days={daysUntil(post.applicationDeadline)} />
    </div>
  );
}

function Deadline({ iso, days }: { iso?: string; days: number | null }) {
  if (!iso) return null;
  // Under a week reads as urgent; beyond that it is neutral meta, not pressure.
  const urgent = days !== null && days <= 7;
  return (
    <Pill tone={urgent ? "urgent" : "meta"}>
      Deadline {absoluteDay(iso)}
      {days !== null && days >= 0 ? ` · ${days} days` : ""}
    </Pill>
  );
}

/* ---------------- Footer ---------------- */

function Footer({ post, actions }: { post: Post; actions: PostCardActions }) {
  const reward = post.rewards?.[0];

  return (
    <div className="card__footer">
      {reward ? (
        <Pill tone="reward">
          <i className="fa-solid fa-sparkles" aria-hidden="true" />
          {reward.label === "Award" || reward.label === "Pay"
            ? reward.value
            : `${reward.value} ${reward.label.toLowerCase()}`}
        </Pill>
      ) : null}

      <span className="card__footer-spacer" />

      <StateAction post={post} actions={actions} />
    </div>
  );
}

function StateAction({ post, actions }: { post: Post; actions: PostCardActions }) {
  switch (post.postType) {
    case "event":
      if (post.isRSVPd) {
        return (
          <>
            <Pill tone="state">
              <i className="fa-solid fa-check" aria-hidden="true" /> Going
            </Pill>
            {post.groupChatId ? (
              <HandoffButton label="Group chat" onOpen={() => openTelegram("https://t.me/")} />
            ) : null}
          </>
        );
      }
      return <Button onClick={() => actions.onRSVP?.(post)}>RSVP</Button>;

    case "activity":
      return post.isJoinedByMe ? (
        <Pill tone="state">
          <i className="fa-solid fa-check" aria-hidden="true" /> Joined
        </Pill>
      ) : (
        <Button onClick={() => actions.onJoin?.(post)}>Join</Button>
      );

    case "rival":
      if (post.isOpen === false) {
        return <Pill tone="state">Accepted</Pill>;
      }
      return (
        <>
          <Button kind="secondary" onClick={() => actions.onDeclineRival?.(post)}>
            Decline
          </Button>
          <Button onClick={() => actions.onAcceptRival?.(post)}>Accept</Button>
        </>
      );

    case "scholarship":
    case "job":
      return post.isAppliedByMe ? (
        <Pill tone="state">
          <i className="fa-solid fa-check" aria-hidden="true" /> Applied
        </Pill>
      ) : (
        <Button onClick={() => actions.onApply?.(post)}>
          {post.postType === "scholarship" ? "Apply — 1 tap" : "Apply"}
        </Button>
      );

    default:
      return (
        <>
          <button
            type="button"
            className="card__like t-caption"
            onClick={(e) => {
              e.stopPropagation();
              actions.onLike?.(post);
            }}
          >
            <i
              className={post.isLikedByMe ? "fa-solid fa-heart" : "fa-regular fa-heart"}
              style={{ color: post.isLikedByMe ? "var(--error)" : undefined }}
              aria-hidden="true"
            />{" "}
            {compactCount(post.likeCount)}
          </button>
          <HandoffButton label="Share" onOpen={() => openTelegram("https://t.me/share/url?url=")} />
        </>
      );
  }
}

function titleCase(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
